/**
 * The rundown list: loading, error, empty and populated states, and a create
 * button. `rundowns`, `loading` and `error` are independent props
 * (docs/testing/components.md): a refetch with rows on screen keeps them
 * there rather than flashing back to the loading state.
 */
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import type { RundownSummary } from '../api/rundowns.js';
import { isPlainClick } from '../lib/links.js';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table.js';

export type RundownListProps = {
  readonly rundowns: readonly RundownSummary[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly onCreate: () => void;
  readonly onRetry: () => void;
  /** A plain click on a rundown's name opens it in-app; a modified click is the link's (new tab). */
  readonly onOpen: (rundownId: string) => void;
};

/** Where the editor for a rundown lives. */
export function rundownHref(rundownId: string): string {
  return `/rundowns/${encodeURIComponent(rundownId)}`;
}

export function RundownList({ rundowns, loading, error, onCreate, onRetry, onOpen }: RundownListProps) {
  return (
    <Card data-testid="rundown-list">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Rundowns</CardTitle>
          <CardDescription>Every rundown in this tenant.</CardDescription>
        </div>
        <Button onClick={onCreate} data-testid="create-rundown">
          <PlusIcon />
          New rundown
        </Button>
      </CardHeader>
      <CardContent>
        {error !== null ? (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load rundowns: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        ) : null}
        {loading && rundowns.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="rundowns-loading">
            Loading rundowns…
          </p>
        ) : null}
        {!loading && error === null && rundowns.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="rundowns-empty">
            No rundowns yet. Create the first one.
          </p>
        ) : null}
        {rundowns.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-80">Id</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rundowns.map((r) => (
                <TableRow key={r.rundownId} data-testid="rundown-row">
                  <TableCell className="font-medium">
                    <a
                      href={rundownHref(r.rundownId)}
                      className="hover:underline"
                      data-testid="open-rundown"
                      onClick={(event) => {
                        if (!isPlainClick(event)) return;
                        event.preventDefault();
                        onOpen(r.rundownId);
                      }}
                    >
                      {r.name}
                    </a>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{r.rundownId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
